import type { NotebookCell as RenderedCell } from "@/app-covi"
import type { CoviActionItem, CoviChatItem } from "@/app-covi/model"
import type { JSONContent } from "@tiptap/react"

import type { NotebookAppearance } from "./notebookCanvas"

/** Frozen source cells accompany an editable document and bounded private Sofia chat. */
export const NOTEBOOK_SNAPSHOT_VERSION = 1 as const

/** Upper bound on a serialized snapshot; the server enforces the same cap. */
export const MAX_NOTEBOOK_SNAPSHOT_BYTES = 256_000
const MAX_NOTEBOOK_SNAPSHOT_CELLS = 200

/** Mirrors Sofia's notebook composer while leaving room under the proxy body cap. */
const MAX_NOTEBOOK_TRANSCRIPT_MESSAGES = 60
const MAX_NOTEBOOK_TRANSCRIPT_MESSAGE_CODE_UNITS = 6_000
export const MAX_NOTEBOOK_TRANSCRIPT_BYTES = 90_000

interface NotebookTranscriptMessage {
  role: "user" | "assistant"
  content: string
}

export type NotebookCell =
  | { kind: "prompt"; text: string }
  | {
      kind: "research"
      steps: Array<{ name: string; status: string; is_error: boolean }>
    }
  | {
      kind: "table"
      title: string | null
      columns: string[]
      rows: Record<string, unknown>[]
      source: "chart" | "narrative"
    }
  | { kind: "chart"; title: string; spec: unknown }
  | { kind: "text"; text: string }
  | {
      kind: "action"
      action_kind: string
      status: string
      detail?: string
      href?: string
    }

export interface NotebookSnapshot {
  version: typeof NOTEBOOK_SNAPSHOT_VERSION
  cells: NotebookCell[]
}

export interface NotebookSummary {
  id: string
  title: string
  workspace_id: string
  created_at: string
  updated_at: string
  cell_count: number
  /** Email of the user who saved it; resolved to a display name by the organization module. */
  created_by: string | null
  audience?: "private" | "workspace" | "organization" | "public"
}

export interface NotebookDocumentSection {
  heading: string
  body: string
  /** Indexes into `snapshot.cells` for the tables and charts this section embeds. */
  embed_cells: number[]
}

/** Sofia's write-up of the thread, composed when the notebook is saved. */
export interface NotebookDocument {
  content?: JSONContent
  appearance?: NotebookAppearance
  media_version?: number
  version: 1
  title: string
  summary: string
  sections: NotebookDocumentSection[]
  caveats: string[]
  /** Saved evidence omitted from the document and its export. */
  excluded_cells?: number[]
}

export interface Notebook extends NotebookSummary {
  schedule_error?: string
  locked?: boolean
  revision_id?: string | null
  assets?: Record<string, string>
  snapshot: NotebookSnapshot
  document?: NotebookDocument | null
  /** Set by the authenticated frontend proxy after the Sofia editing API is deployed. */
  editing_enabled?: boolean
  /** False when the caller is a viewer, not the owner: no editing, sharing, rename or delete. */
  can_edit?: boolean
  /** Private authoring history, excluded from shared notebook responses. */
  chat?: Array<{ role: "user" | "assistant"; content: string }>
}

/** New Sofia document fields stay visible, but require an updated editor contract. */
export function supportsNotebookDocumentEditing(
  document: NotebookDocument | null | undefined,
): boolean {
  if (document?.version !== 1) {
    return false
  }
  const fields = new Set([
    "version",
    "title",
    "summary",
    "sections",
    "caveats",
    "excluded_cells",
    "content",
    "appearance",
    "media_version",
  ])
  const sectionFields = new Set(["heading", "body", "embed_cells"])
  return (
    (document.media_version === undefined || document.media_version === 1) &&
    Object.keys(document).every((key) => fields.has(key)) &&
    document.sections.every((section) =>
      Object.keys(section).every((key) => sectionFields.has(key)),
    )
  )
}

const TITLE_MAX_LENGTH = 80

export function defaultNotebookTitle(cells: RenderedCell[]): string {
  const prompt = cells.find((cell) => cell.kind === "prompt")
  const text = prompt?.kind === "prompt" ? prompt.text.trim() : ""
  if (!text) {
    return "Untitled notebook"
  }
  return text.length > TITLE_MAX_LENGTH
    ? `${text.slice(0, TITLE_MAX_LENGTH - 1)}…`
    : text
}

export function buildNotebookSnapshot(cells: RenderedCell[]): NotebookSnapshot {
  const snapshotCells: NotebookCell[] = []
  for (const cell of cells) {
    switch (cell.kind) {
      case "prompt":
        snapshotCells.push({ kind: "prompt", text: cell.text })
        break
      case "research":
        snapshotCells.push({
          kind: "research",
          steps: cell.items.map((item) => ({
            name: item.name,
            status: item.status,
            is_error: item.isError,
          })),
        })
        break
      case "table":
        snapshotCells.push({
          kind: "table",
          title: cell.title,
          columns: cell.columns,
          rows: cell.rows,
          source: cell.source,
        })
        break
      case "visualization": {
        const title = cell.item.proposal.args.title
        snapshotCells.push({
          kind: "chart",
          title: typeof title === "string" ? title : "",
          spec: cell.item.proposal.args.spec,
        })
        break
      }
      case "narrative":
        snapshotCells.push({ kind: "text", text: cell.item.text })
        break
      case "action":
        snapshotCells.push({
          kind: "action",
          action_kind: cell.item.proposal.kind,
          status: cell.item.status,
          ...(cell.item.detail ? { detail: cell.item.detail } : {}),
          ...(cell.item.href ? { href: cell.item.href } : {}),
        })
        break
      case "saved-action":
        snapshotCells.push({
          kind: "action",
          action_kind: cell.actionKind,
          status: cell.status,
          ...(cell.detail ? { detail: cell.detail } : {}),
          ...(cell.href ? { href: cell.href } : {}),
        })
        break
      case "error":
        // Errors are chat noise, not durable content; a saved notebook omits them.
        break
      default:
        break
    }
  }
  return {
    version: NOTEBOOK_SNAPSHOT_VERSION,
    cells: snapshotCells.slice(-MAX_NOTEBOOK_SNAPSHOT_CELLS),
  }
}

function serializedSnapshotBytes(snapshot: NotebookSnapshot): number {
  return new TextEncoder().encode(JSON.stringify(snapshot)).length
}

/**
 * The snapshot a save sends, or null when it cannot fit the server cap. Kept
 * table citations can push a chat that fit with label-only cells past the cap,
 * so before rejecting the save this projects again without them: a chat that
 * saved before citations were kept still saves, just without those links.
 */
export function buildFittingNotebookSnapshot(
  project: (options: { tableLinks: boolean }) => RenderedCell[],
): NotebookSnapshot | null {
  for (const tableLinks of [true, false]) {
    const snapshot = buildNotebookSnapshot(project({ tableLinks }))
    if (serializedSnapshotBytes(snapshot) <= MAX_NOTEBOOK_SNAPSHOT_BYTES) {
      return snapshot
    }
  }
  return null
}

function serializedTranscriptBytes(
  messages: NotebookTranscriptMessage[],
): number {
  return new TextEncoder().encode(JSON.stringify(messages)).length
}

function truncateMessageCodeUnits(content: string): string {
  if (content.length <= MAX_NOTEBOOK_TRANSCRIPT_MESSAGE_CODE_UNITS) {
    return content
  }
  const truncated = content.slice(0, MAX_NOTEBOOK_TRANSCRIPT_MESSAGE_CODE_UNITS)
  const lastCodeUnit = truncated.charCodeAt(truncated.length - 1)
  return lastCodeUnit >= 0xd800 && lastCodeUnit <= 0xdbff
    ? truncated.slice(0, -1)
    : truncated
}

function truncateMessageToFit(
  message: NotebookTranscriptMessage,
  newerMessages: NotebookTranscriptMessage[],
): NotebookTranscriptMessage | null {
  const characters = Array.from(message.content)
  let low = 0
  let high = characters.length
  while (low < high) {
    const midpoint = Math.ceil((low + high) / 2)
    const candidate = {
      ...message,
      content: characters.slice(0, midpoint).join(""),
    }
    if (
      serializedTranscriptBytes([candidate, ...newerMessages]) <=
      MAX_NOTEBOOK_TRANSCRIPT_BYTES
    ) {
      low = midpoint
    } else {
      high = midpoint - 1
    }
  }
  return low > 0
    ? { ...message, content: characters.slice(0, low).join("") }
    : null
}

/**
 * Keep the newest transcript turns within Sofia's per-message limits and the
 * frontend proxy's total request budget. Tool/action items are represented by
 * the snapshot and do not belong in the prose composer input.
 */
export function buildNotebookTranscriptMessages(
  items: CoviChatItem[],
): NotebookTranscriptMessage[] {
  const eligible = items.flatMap((item): NotebookTranscriptMessage[] => {
    if (
      (item.kind !== "user" && item.kind !== "assistant") ||
      item.text.trim().length === 0
    ) {
      return []
    }
    return [
      {
        role: item.kind,
        content: truncateMessageCodeUnits(item.text),
      },
    ]
  })
  const recent = eligible.slice(-MAX_NOTEBOOK_TRANSCRIPT_MESSAGES)
  const bounded: NotebookTranscriptMessage[] = []
  for (let index = recent.length - 1; index >= 0; index -= 1) {
    const message = recent[index]
    if (!message) {
      continue
    }
    if (
      serializedTranscriptBytes([message, ...bounded]) <=
      MAX_NOTEBOOK_TRANSCRIPT_BYTES
    ) {
      bounded.unshift(message)
      continue
    }
    const truncated = truncateMessageToFit(message, bounded)
    if (!truncated) {
      break
    }
    bounded.unshift(truncated)
    break
  }
  return bounded
}

type CoviToolItem = Extract<CoviChatItem, { kind: "tool" }>

/**
 * Rebuild render-ready cells from a saved snapshot. Charts become settled
 * `render_visualization` items so the same safe chart card draws them; the
 * spec is re-validated by the card, so a stale or invalid spec fails closed.
 */
export function cellsFromSnapshot(
  notebookId: string,
  snapshot: NotebookSnapshot,
): RenderedCell[] {
  const settledAt = 0
  return snapshot.cells.flatMap((cell, index): RenderedCell[] => {
    const id = `${notebookId}:${index}`
    switch (cell.kind) {
      case "prompt":
        return [{ kind: "prompt", id, text: cell.text }]
      case "research":
        return [
          {
            kind: "research",
            id,
            items: cell.steps.map(
              (step, stepIndex): CoviToolItem => ({
                kind: "tool",
                id: `${id}:${stepIndex}`,
                name: step.name,
                summary: "",
                status: step.status,
                running: false,
                isError: step.is_error,
                isResearch: true,
              }),
            ),
          },
        ]
      case "table":
        return [
          {
            kind: "table",
            id,
            title: cell.title,
            columns: cell.columns,
            rows: cell.rows,
            source: cell.source,
          },
        ]
      case "chart": {
        const item: CoviActionItem = {
          kind: "action",
          id,
          proposal: {
            kind: "render_visualization",
            args: { title: cell.title, spec: cell.spec },
            rationale: "",
            proposal_id: id,
            requires_confirmation: false,
          },
          status: "done",
          proposedAt: settledAt,
          settledAt,
        }
        return [{ kind: "visualization", id, item }]
      }
      case "text":
        return [
          {
            kind: "narrative",
            id,
            item: { kind: "assistant", id, text: cell.text },
          },
        ]
      case "action":
        return [
          {
            kind: "saved-action",
            id,
            actionKind: cell.action_kind,
            status: cell.status,
            detail: cell.detail ?? null,
            href: cell.href ?? null,
          },
        ]
      default:
        return []
    }
  })
}
